// RigSentinel Context Engine — Engineering Snapshot helpers (Phase RCE-4)
//
// Centralises two refinements approved at the start of RCE-4:
//
//   1. **Engineering Snapshot ID** — every frozen snapshot receives a
//      permanent monotonic identifier (`ENG-000001`, `ENG-000002`, …). The
//      counter is persisted on `rsc.snapshotSeq.v1` so IDs never collide
//      across reloads.
//
//   2. **Source Modules** — every snapshot records which master modules
//      contributed data (Project / Well / Rig / BHA / Drill String /
//      Mud Program / Daily Operations). Surfaces the engineering
//      provenance for future audit + AI consumers.
//
// Pure helpers — no React, no UI. Imported by every module that produces a
// frozen engineering snapshot (Daily Ops, Drilling Fluid Management, Kill
// Sheets).

import { scoped } from "@/lib/accountScope";

const SNAPSHOT_SEQ_KEY  = "rsc.snapshotSeq.v1";
const SNAPSHOT_ID_PREFIX = "ENG-";
const SNAPSHOT_ID_PAD    = 6;     // ENG-000001 → ENG-999999, then auto-grows

/** Next monotonic snapshot ID — `ENG-000001`, `ENG-000002`, … */
export function nextSnapshotId() {
  const raw = scoped.getItem(SNAPSHOT_SEQ_KEY);
  let n = Number(raw);
  if (!Number.isFinite(n) || n < 0) n = 0;
  n += 1;
  scoped.setItem(SNAPSHOT_SEQ_KEY, String(n));
  const num = String(n).padStart(SNAPSHOT_ID_PAD, "0");
  return `${SNAPSHOT_ID_PREFIX}${num}`;
}

/** Diagnostic — read the last issued sequence WITHOUT incrementing. */
export function peekSnapshotSeq() {
  const raw = scoped.getItem(SNAPSHOT_SEQ_KEY);
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

/** Test helper — never called from app code. */
export function _resetSnapshotSeq() {
  scoped.removeItem(SNAPSHOT_SEQ_KEY);
}

// ---------------------------------------------------------------------------
// SOURCE MODULES
// ---------------------------------------------------------------------------
// Canonical list of master-record families that can contribute to a
// frozen engineering snapshot. The order matters — UI / Context Inspector
// renders them in this sequence.
export const SOURCE_MODULES = Object.freeze([
  "Project",
  "Well",
  "Rig",
  "BHA",
  "Drill String",
  "Mud Program",
  "Daily Operations",
  "Settings",
]);

/**
 * Infer which Source Modules contributed to a snapshot given the
 * `sessionContextIds` block (which records the master record IDs active at
 * freeze time). The Daily Operations entry is added by the caller when the
 * snapshot was produced INSIDE a Daily Ops document. The Settings entry is
 * added when any `settings.*` field was resolved into the snapshot (RCE-4.5).
 *
 * Always returns the list in canonical SOURCE_MODULES order.
 */
export function deriveSourceModules(sessionContextIds = {}, opts = {}) {
  const set = new Set();
  if (sessionContextIds.projectId)     set.add("Project");
  if (sessionContextIds.wellId)        set.add("Well");
  if (sessionContextIds.rigId)         set.add("Rig");
  if (sessionContextIds.bhaId)         set.add("BHA");
  if (sessionContextIds.drillStringId) set.add("Drill String");
  if (sessionContextIds.mudProgramId)  set.add("Mud Program");
  if (opts.includeDailyOps)            set.add("Daily Operations");
  if (opts.includeSettings)            set.add("Settings");
  return SOURCE_MODULES.filter((m) => set.has(m));
}

/**
 * Stamp the standard RCE-1.0 envelope onto an existing snapshot object.
 * Mutates and returns the snapshot.
 *
 *   `snap.snapshotId`    — monotonic ENG-xxxxxx identifier
 *   `snap.capturedAt`    — ISO timestamp (set if missing)
 *   `snap.sourceModules` — array (filled from sessionContextIds + opts)
 */
export function stampEngineeringMetadata(snap, opts = {}) {
  if (!snap || typeof snap !== "object")
    throw new Error("stampEngineeringMetadata: snapshot is missing");
  if (!snap.snapshotId) snap.snapshotId = nextSnapshotId();
  if (!snap.capturedAt) snap.capturedAt = new Date().toISOString();
  snap.sourceModules = deriveSourceModules(snap.sessionContextIds || {}, opts);
  // Auto-detect Settings contribution from the snapshot's confidence map.
  if (!opts.includeSettings && snap.confidence) {
    const hasSettings = Object.keys(snap.confidence).some((p) =>
      p.startsWith("settings.") &&
      snap.confidence[p] !== "unknown" && snap.confidence[p] !== "conflict");
    if (hasSettings && !snap.sourceModules.includes("Settings")) {
      snap.sourceModules = deriveSourceModules(snap.sessionContextIds || {},
        { ...opts, includeSettings: true });
    }
  }
  return snap;
}
